// What he plays, as notes on a page rather than sound.
//
// Split out of piano.js so the same composition can be heard two ways:
// the browser schedules it through WebAudio while somebody is watching,
// and an offline renderer turns it into a wav long afterwards. Sam wanted
// a record of his playing, and the elegant version of that is not a
// recorder listening for a bird who plays a few times a day. The music is
// DETERMINISTIC: tier plus seed gives the same notes every time, so a few
// numbers per sitting are the recording, and any past sitting can be
// rendered whenever you like.
//
// Nothing in here touches WebAudio, the DOM or node, so both callers can
// import it and neither can drift from the other.

export const SCALE = [220, 246.9, 261.6, 293.7, 329.6, 392, 440, 493.9, 523.3]

/** Small deterministic PRNG. Same seed, same performance, forever. */
export function mulberry(seed) {
  let a = seed >>> 0
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * One phrase, pushed onto `out` as {freq, at, dur, gain}. Returns when
 * the phrase ends, so a caller can leave the gap a player would leave.
 * Hesitation IS the early instrument: the gaps are the learning.
 */
export function phrase(out, tier, rand, t) {
  const pick = (arr) => arr[Math.floor(rand() * arr.length)]
  const note = (freq, at, dur, gain) => out.push({ freq, at, dur, gain })

  if (tier === 'plinking') {
    const n = 1 + Math.floor(rand() * 3)
    for (let i = 0; i < n; i++) {
      let f = pick(SCALE)
      if (rand() < 0.3) f *= rand() < 0.5 ? 0.944 : 1.06 // the sour neighbour
      note(f, t, 1.2, 0.5)
      t += 0.5 + rand() * 1.4
    }
    return t + 1.2 + rand() * 2.5
  }
  if (tier === 'picking') {
    let idx = Math.floor(rand() * SCALE.length)
    const n = 3 + Math.floor(rand() * 4)
    for (let i = 0; i < n; i++) {
      idx = Math.max(0, Math.min(SCALE.length - 1, idx + (rand() < 0.5 ? 1 : -1)))
      note(SCALE[idx], t, 1.0, 0.45)
      t += 0.42 + rand() * 0.5
      if (rand() < 0.12) { t += 1.0; i = Math.max(0, i - 2) } // the restart
    }
    return t + 0.8 + rand() * 1.6
  }
  // pieces and tunes share the flowing line; tunes add the left hand.
  const withChords = tier === 'tunes'
  let idx = 2 + Math.floor(rand() * 4)
  const n = 6 + Math.floor(rand() * 6)
  if (withChords) {
    const root = SCALE[Math.floor(rand() * 3)] / 2
    note(root, t, 2.4, 0.3)
    note(root * 1.5, t + 0.02, 2.4, 0.2)
  }
  for (let i = 0; i < n; i++) {
    idx = Math.max(0, Math.min(SCALE.length - 1, idx + Math.round((rand() - 0.5) * 3)))
    note(SCALE[idx], t, 0.9, 0.4)
    t += withChords ? 0.3 + rand() * 0.25 : 0.34 + rand() * 0.3
    if (withChords && i === Math.floor(n / 2)) {
      const root = SCALE[Math.floor(rand() * 3)] / 2
      note(root, t, 2.2, 0.28)
    }
  }
  return t + (withChords ? 0.6 + rand() * 1.0 : 0.9 + rand() * 1.4)
}

/**
 * A whole sitting: every note in `seconds` of playing at a tier, from a
 * seed. The browser asks for a few seconds at a time as it goes; the
 * renderer asks for the lot in one call. Same notes either way.
 */
export function score(tier, seed, seconds, startAt = 0) {
  const rand = mulberry(seed * 2654435761)
  const out = []
  let t = startAt
  while (t < startAt + seconds) t = phrase(out, tier, rand, t)
  return out
}
